import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { planningApi as P, JOB_STATUS_STYLE, PRIORITY_STYLE } from "@/lib/planning";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Search, Loader2, Wrench } from "lucide-react";

export default function Planning() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ statuses: [], route: [] });
  const [stats, setStats] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [machinesOpen, setMachinesOpen] = useState(false);
  const [machines, setMachines] = useState([]);
  const [newMachine, setNewMachine] = useState({ machine_id: "", name: "", stage: "printing" });

  const load = async () => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([P.stats(), P.jobs({ search: search || undefined, status })]);
      setStats(s); setRows(list);
    } finally { setLoading(false); }
  };
  useEffect(() => { P.meta().then(setMeta); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const openMachines = async () => { setMachines(await P.machines()); setMachinesOpen(true); };
  const addMachine = async () => {
    if (!newMachine.machine_id) { toast.error("Enter a machine id"); return; }
    try { await P.addMachine(newMachine); setMachines(await P.machines()); setNewMachine({ machine_id: "", name: "", stage: "printing" }); toast.success("Machine added"); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6" data-testid="planning-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Production Planning</h2>
          <p className="mt-1 text-sm text-slate-500">Turn Confirmed Sales Orders into executable Production Jobs, then release them to the factory.</p>
        </div>
        <div className="flex gap-2">
          {can("production_planning", "create") && <Button variant="outline" onClick={openMachines} data-testid="manage-machines-btn"><Wrench className="mr-2 h-4 w-4" />Machines</Button>}
          {can("production_planning", "create") && <Button onClick={() => navigate("/production-planning/jobs/new")} data-testid="new-job-btn" className="bg-indigo-600 text-white hover:bg-indigo-700"><Plus className="mr-2 h-4 w-4" />New Job</Button>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="p-4" data-testid="plan-stat-planning"><p className="text-xs uppercase tracking-wider text-slate-500">In planning</p><p className="mt-1 font-heading text-2xl font-bold text-blue-600">{stats.planning ?? 0}</p></Card>
        <Card className="p-4" data-testid="plan-stat-released"><p className="text-xs uppercase tracking-wider text-slate-500">Released / running</p><p className="mt-1 font-heading text-2xl font-bold text-indigo-600">{stats.released ?? 0}</p></Card>
        <Card className="p-4" data-testid="plan-stat-meters"><p className="text-xs uppercase tracking-wider text-slate-500">Planned meters</p><p className="mt-1 font-heading text-2xl font-bold text-slate-900">{(stats.planned_meters ?? 0).toLocaleString("en-IN")}</p></Card>
        <Card className="p-4" data-testid="plan-stat-available"><p className="text-xs uppercase tracking-wider text-slate-500">SOs to plan</p><p className="mt-1 font-heading text-2xl font-bold text-amber-600">{stats.available_orders ?? 0}</p></Card>
      </div>

      <Card className="p-4">
        <div className="mb-4 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search job / SO / customer" className="pl-9" data-testid="job-search-input" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-52" data-testid="job-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase text-slate-500">Job No</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Customer</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Fabric</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Target</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Seq</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Priority</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Target date</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate(`/production-planning/jobs/${r.id}`)} data-testid={`job-row-${r.job_no}`}>
                    <TableCell className="font-medium text-slate-800">{r.job_no}<span className="block text-xs text-slate-400">from {r.so_no}</span></TableCell>
                    <TableCell className="text-slate-700">{r.customer?.company_name}</TableCell>
                    <TableCell className="text-slate-600">{r.item?.fabric_type}</TableCell>
                    <TableCell className="tabular-nums text-slate-700">{r.target_meters} m</TableCell>
                    <TableCell className="text-slate-600">{r.sequence || "—"}</TableCell>
                    <TableCell><Badge className={`hover:bg-inherit ${PRIORITY_STYLE[r.priority]}`}>{r.priority}</Badge></TableCell>
                    <TableCell className="text-slate-600">{r.target_date || "—"}</TableCell>
                    <TableCell><Badge className={`hover:bg-inherit ${JOB_STATUS_STYLE[r.status]}`}>{r.status}</Badge></TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={8} className="py-12 text-center text-slate-400" data-testid="job-empty">No production jobs yet. Create one from a Confirmed Sales Order.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={machinesOpen} onOpenChange={setMachinesOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Machine registry</DialogTitle><DialogDescription>Machines allocated here are published to Production for scheduling.</DialogDescription></DialogHeader>
          <div className="max-h-52 space-y-1 overflow-y-auto">
            {machines.map((m) => (
              <div key={m.machine_id} className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-sm" data-testid={`machine-${m.machine_id}`}>
                <span className="font-medium text-slate-800">{m.machine_id} · {m.name}</span>
                <Badge className="bg-white text-slate-600">{m.stage}</Badge>
              </div>
            ))}
            {machines.length === 0 && <p className="py-4 text-center text-sm text-slate-400">No machines registered yet.</p>}
          </div>
          <div className="grid grid-cols-3 gap-2 border-t border-slate-100 pt-3">
            <div className="space-y-1"><Label className="text-xs">Machine ID</Label><Input value={newMachine.machine_id} onChange={(e) => setNewMachine({ ...newMachine, machine_id: e.target.value })} data-testid="machine-id-input" /></div>
            <div className="space-y-1"><Label className="text-xs">Name</Label><Input value={newMachine.name} onChange={(e) => setNewMachine({ ...newMachine, name: e.target.value })} /></div>
            <div className="space-y-1"><Label className="text-xs">Stage</Label>
              <Select value={newMachine.stage} onValueChange={(v) => setNewMachine({ ...newMachine, stage: v })}>
                <SelectTrigger data-testid="machine-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta.route || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <DialogFooter><Button onClick={addMachine} data-testid="add-machine-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">Add machine</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
